import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const NPSCalculator = () => {
  const [promoters, setPromoters] = useState<string>("");
  const [passives, setPassives] = useState<string>("");
  const [detractors, setDetractors] = useState<string>("");
  const [result, setResult] = useState<{ nps: number; total: number } | null>(null);

  const calculate = () => {
    const promotersNum = parseFloat(promoters) || 0;
    const passivesNum = parseFloat(passives) || 0;
    const detractorsNum = parseFloat(detractors) || 0;
    
    const total = promotersNum + passivesNum + detractorsNum;
    
    if (total === 0) {
      setResult(null);
      return;
    }

    const promoterPercent = (promotersNum / total) * 100;
    const detractorPercent = (detractorsNum / total) * 100;
    const nps = promoterPercent - detractorPercent;
    
    setResult({ nps, total });
  };

  const reset = () => {
    setPromoters("");
    setPassives("");
    setDetractors("");
    setResult(null);
  };

  const getAssessment = (nps: number) => {
    if (nps >= 70) return { label: "World Class", color: "text-green-600" };
    if (nps >= 50) return { label: "Excellent", color: "text-green-500" };
    if (nps >= 30) return { label: "Good", color: "text-yellow-600" };
    if (nps >= 0) return { label: "Needs Work", color: "text-orange-500" };
    return { label: "Critical", color: "text-red-600" };
  };

  const relatedTools = getRelatedTools("nps-calculator");

  return (
    <ToolPageLayout
      title="NPS Calculator"
      description="Calculate your Net Promoter Score to measure customer loyalty and predict business growth."
      metaDescription="NPS (Net Promoter Score) measures customer loyalty by asking how likely customers are to recommend you. It's a leading indicator of growth potential and customer satisfaction."
      icon={Users}
      slug="nps-calculator"
      formula={{
        equation: "NPS = % Promoters - % Detractors",
        variables: [
          { name: "Promoters (9-10)", description: "Customers who rate you 9 or 10 out of 10" },
          { name: "Passives (7-8)", description: "Satisfied but not enthusiastic customers" },
          { name: "Detractors (0-6)", description: "Unhappy customers who may damage your brand" },
        ]
      }}
      example={{
        inputs: [
          { label: "Promoters", value: "60" },
          { label: "Passives", value: "25" },
          { label: "Detractors", value: "15" },
        ],
        output: "+45 NPS",
        interpretation: "An NPS of 45 is considered good. Above 50 is excellent, above 70 is world-class."
      }}
      improvements={[
        "Follow up with detractors to understand and resolve issues",
        "Convert passives by exceeding expectations",
        "Ask promoters for referrals and reviews",
        "Close the feedback loop by acting on insights",
        "Segment NPS by customer type to find patterns",
        "Track NPS trends over time, not just point-in-time scores",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Analytics", href: "/analytics" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate NPS</CardTitle>
          <CardDescription>Enter your survey responses</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="promoters">Promoters (9-10 ratings)</Label>
            <Input
              id="promoters"
              type="number"
              placeholder="60"
              value={promoters}
              onChange={(e) => setPromoters(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="passives">Passives (7-8 ratings)</Label>
            <Input
              id="passives"
              type="number"
              placeholder="25"
              value={passives}
              onChange={(e) => setPassives(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="detractors">Detractors (0-6 ratings)</Label>
            <Input
              id="detractors"
              type="number"
              placeholder="15"
              value={detractors}
              onChange={(e) => setDetractors(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculate} className="flex-1">
              Calculate NPS
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result !== null && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Net Promoter Score</p>
                <p className={`text-4xl font-bold ${getAssessment(result.nps).color}`}>
                  {result.nps >= 0 ? '+' : ''}{result.nps.toFixed(0)}
                </p>
                <p className={`text-sm mt-3 ${getAssessment(result.nps).color}`}>
                  {getAssessment(result.nps).label}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Based on {result.total} responses
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default NPSCalculator;
